/* eslint-disable max-lines */
import React from 'react';
import Select, { MenuPlacement } from 'react-select';
import Creatable from 'react-select/creatable';
import { borderRadiusPalette } from 'utils/styleConstants';
import { useTheme } from 'providers/ThemeProvider';
import { SelectWrapper, FeedbackText, createSmallStyles, createNormalStyles } from './styled';

export interface DropdownProps {
  id: string;
  options: any[];
  isMulti?: boolean;
  disabled?: boolean;
  placeholder?: string;
  small?: boolean;
  onChange?: (option: any, info?: any) => void;
  onCreateOption?: (option: any) => void;
  onFocus?: () => void;
  value?: any;
  isClearable?: boolean;
  name?: string;
  hasFeedback?: boolean;
  feedbackMessage?: string;
  feedbackColor?: string;
  noBorder?: boolean;
  defaultDropdownValue?: any;
  creatable?: boolean;
  isPortal?: boolean;
  customHeight?: string;
  menuIsOpen?: boolean;
  minWidthLabel?: string;
  backgroundColor?: string;
  fontSize?: string;
  valueContainerPadding?: string;
  singleValueMargin?: string;
  margin?: string;
  menuWidth?: string;
  width?: string;
  height?: string;
  displayWrapper?: string;
  overflowWidth?: string;
  closeMenuOnScroll?: EventListener;
  setRef?: any;
  onInputChange?: (val: string) => void;
}

const Dropdown = (props: DropdownProps) => {
  const { isDarkMode, theme: appTheme } = useTheme();
  const {
    id = '',
    options,
    isMulti,
    disabled,
    placeholder,
    onChange,
    onCreateOption,
    onFocus,
    small,
    isClearable,
    value,
    name,
    hasFeedback,
    feedbackMessage,
    feedbackColor = 'red',
    noBorder,
    defaultDropdownValue,
    creatable,
    menuIsOpen,
    isPortal = true,
    customHeight,
    minWidthLabel = small ? '' : '200px',
    backgroundColor,
    fontSize,
    valueContainerPadding,
    singleValueMargin,
    margin,
    menuWidth = 'fit-content',
    width,
    displayWrapper,
    overflowWidth,
    closeMenuOnScroll,
    setRef,
    onInputChange,
  } = props;
  // Get styles based on size
  const customStyles = small
    ? createSmallStyles(
        isDarkMode,
        appTheme,
        customHeight,
        fontSize,
        noBorder,
        feedbackColor,
        hasFeedback,
        backgroundColor,
        disabled,
        minWidthLabel,
        singleValueMargin,
        overflowWidth,
        menuWidth,
      )
    : createNormalStyles(
        isDarkMode,
        appTheme,
        customHeight,
        fontSize,
        feedbackColor,
        hasFeedback,
        backgroundColor,
        disabled,
        minWidthLabel,
        valueContainerPadding,
        singleValueMargin,
        overflowWidth,
        menuWidth,
        width,
        margin,
      );

  const propsComponent = {
    name: name,
    className: 'react-select',
    styles: customStyles,
    isClearable: isClearable,
    onInputChange: onInputChange,
    onChange: onChange,
    onCreateOption: onCreateOption,
    onFocus: onFocus,
    value: value,
    theme: (selectTheme: any) => ({
      ...selectTheme,
      borderRadius: small ? borderRadiusPalette.dropDown.small : borderRadiusPalette.dropDown.normal,
      colors: {
        ...selectTheme.colors,
        primary25: isDarkMode ? appTheme.colorPalette.grey.grey8 : appTheme.colorPalette.turquoise.background,
        primary: feedbackColor && hasFeedback ? feedbackColor : appTheme.colorPalette.turquoise.light,
        neutral10: isDarkMode ? appTheme.colorPalette.grey.grey8 : appTheme.colorPalette.turquoise.background,
        neutral30: feedbackColor && hasFeedback ? feedbackColor : undefined,
        neutral80: isDarkMode ? appTheme.colorPalette.white : appTheme.colorPalette.black,
        neutral20: isDarkMode ? appTheme.colorPalette.grey.grey6 : appTheme.colorPalette.grey.grey5,
        neutral0: isDarkMode ? appTheme.colorPalette.grey.grey3 : appTheme.colorPalette.white,
      },
    }),
    isMulti: isMulti,
    isDisabled: disabled,
    options: options,
    placeholder: placeholder,
    defaultValue: defaultDropdownValue,
    menuIsOpen,
    menuPlacement: 'auto' as MenuPlacement,
    tabSelectsValue: false,
  };

  return (
    <SelectWrapper display={displayWrapper} width={width} data-walkthrough={id}>
      {creatable ? (
        <Creatable {...propsComponent} />
      ) : isPortal ? (
        <Select
          ref={setRef}
          key={`select_${id}_${value}`}
          id={id}
          menuPortalTarget={document.getElementById('react-select-portal')}
          closeMenuOnScroll={closeMenuOnScroll}
          {...propsComponent}
        />
      ) : (
        <Select
          key={`select_${id}_${value}`}
          id={id}
          ref={setRef}
          closeMenuOnScroll={closeMenuOnScroll}
          {...propsComponent}
        />
      )}
      {hasFeedback && (
        <FeedbackText feedbackColor={feedbackColor} maxWidth={minWidthLabel}>
          {feedbackMessage}
        </FeedbackText>
      )}
    </SelectWrapper>
  );
};

export default Dropdown;
